/**
 * Rista API Data Adapter
 *
 * Transforms Rista API menu format into the format expected by the existing UI components.
 * This allows switching to the Rista API without changing the entire UI layer.
 */

/**
 * Tag icon URLs - Maps tag IDs to their icon URLs
 */
const TAG_ICON_URLS = {
  "62a4948861472b337562f38a":"https://cdn.staging.bluetokaicoffee.com/tags/793191752646973445.png",
  "6932636dec4624b7afca0266":"https://cdn.bluetokaicoffee.com/tags/BlueStars.png",
  "62a4948861472b337562f386": "https://cdn.bluetokaicoffee.com/tags/4576261754992587499.png",
  "62a4948861472b337562f387": "https://cdn.bluetokaicoffee.com/tags/980691754992654923.png",
  "62a4948861472b337562f389" : 'https://cdn.bluetokaicoffee.com/tags/7929491754992910776.png'
};

/**
 * List of category IDs to exclude from the menu
 * Add category IDs here to hide them from display
 */
const BANNED_CATEGORY_IDS = new Set([
  '65f5506d7b29bcd7625936c4', // Pour Over Options
  '62b2e9858b79b70a37056ff0', // Addons
  '65f5507655daca276a0220e8', // Bread Options
  '65f55060d3816a75ad4d85c3', // Grind Options
  '6675b47989bb0a5fc7ba9e6a',
  '6675b589df4647dfd813a8ba',
  '674c41ac4df06fbf1f65532b',
  '6675b4f89573c92a935d8bf1',
  '6675b555df4647dfd813a881',
  '682b03f123486fcb93a600bb',
  '667962bd64dae77410d769ba',
  '62a4942771dd9a0b73ce7c84',
  '62a856b46f11c50a6189b797',
  '6655373087a7fb6cca05cbaa'
]);

/**
 * Transform Rista menu data to the format expected by components
 * @param {Object} ristaData - Raw data from Rista API
 * @returns {Object} Transformed data matching the existing component structure
 */
export function transformRistaMenuData(ristaData) {
  const {
    categories = [],
    items = [],
    itemTags = [],
    optionSets = [],
    taxTypes = [],
  } = ristaData;

  // Create lookup maps for quick access
  const itemTagsMap = createItemTagsMap(itemTags);
  const optionSetsMap = createOptionSetsMap(optionSets);
  const itemsMap = createItemsMap(items);

  // Group items by category and subcategory
  const itemsByCategoryMap = groupItemsByCategory(items);

  // Find the "New" tag ID
  const newTagId = findNewTagId(itemTags);

  // Build category structure with products and sort by displayOrder
  const formattedCategories = categories
    .filter(cat => !BANNED_CATEGORY_IDS.has(cat.categoryId)) // Exclude banned categories
    .filter(cat => itemsByCategoryMap.has(cat.categoryId)) // Only include categories with items
    .sort((a, b) => {
      // Sort by displayOrder, treating undefined/null as Infinity (put at end)
      const orderA = a.displayOrder !== undefined && a.displayOrder !== null ? a.displayOrder : Infinity;
      const orderB = b.displayOrder !== undefined && b.displayOrder !== null ? b.displayOrder : Infinity;
      return orderA - orderB;
    })
    .map(cat => formatCategory(cat, itemsByCategoryMap, itemTagsMap, optionSetsMap, itemsMap));

  // Create "Newly Launched" category for products with "New" tag and insert it first
  if (newTagId) {
    const newlyLaunchedCategory = createNewlyLaunchedCategory(
      items,
      itemsByCategoryMap,
      itemTagsMap,
      optionSetsMap,
      itemsMap,
      categories,
      newTagId
    );
    if (newlyLaunchedCategory) {
      formattedCategories.unshift(newlyLaunchedCategory);
    }
  }

  // Find all tag IDs that are actually used by items
  const usedTagIds = new Set();
  items.forEach(item => {
    if (item.itemTagIds && Array.isArray(item.itemTagIds)) {
      item.itemTagIds.forEach(tagId => usedTagIds.add(tagId));
    }
  });

  // Format tags for filtering - only include tags that are used
  const formattedTags = formatTags(itemTags, usedTagIds);

  // Extract all product names for search
  const productNames = items.map(item => item.itemName.toLowerCase());

  return {
    categories: formattedCategories,
    tags: formattedTags,
    productNames,
    taxTypes: Array.isArray(taxTypes) ? taxTypes : [],
    store: null, // No store info in Rista format
  };
}

/**
 * Create a map of item tags by ID
 */
function createItemTagsMap(itemTags) {
  const map = new Map();
  itemTags.forEach(tag => {
    const iconUrl = TAG_ICON_URLS[tag.itemTagId];
    map.set(tag.itemTagId, {
      id: tag.itemTagId,
      name: tag.name,
      tagIcon: iconUrl ? { fullPath: iconUrl } : null, // Include icon URL if available
    });
  });
  return map;
}

/**
 * Create a map of option sets by ID
 */
function createOptionSetsMap(optionSets) {
  const map = new Map();
  optionSets.forEach(optionSet => {
    map.set(optionSet.optionSetId, optionSet);
  });
  return map;
}

/**
 * Create a map of items by ID
 */
function createItemsMap(items) {
  const map = new Map();
  items.forEach(item => {
    map.set(item.itemId, item);
  });
  return map;
}

/**
 * Find the tag ID for the "New" tag
 * @param {Array} itemTags - Array of tag objects
 * @returns {string|null} The tag ID if found, null otherwise
 */
function findNewTagId(itemTags) {
  const newTag = itemTags.find(tag => 
    tag.name && tag.name.toLowerCase().trim() === 'new'
  );
  return newTag ? newTag.itemTagId : null;
}

function getItemTagIds(item) {
  return Array.isArray(item?.itemTagIds) ? item.itemTagIds : [];
}

function hasTag(item, tagId) {
  return Boolean(tagId && getItemTagIds(item).includes(tagId));
}

function mergeUniqueTagIds(...items) {
  const seen = new Set();
  const merged = [];

  items.flat().forEach(item => {
    getItemTagIds(item).forEach(tagId => {
      if (!seen.has(tagId)) {
        seen.add(tagId);
        merged.push(tagId);
      }
    });
  });

  return merged;
}

function formatTagIds(tagIds, itemTagsMap) {
  return tagIds
    .map(tagId => itemTagsMap.get(tagId))
    .filter(tag => tag !== undefined);
}

/**
 * Create a "Newly Launched" category containing products with the "New" tag
 * @param {Array} items - All items from the API
 * @param {Map} itemsByCategoryMap - Map of items grouped by category
 * @param {Map} itemTagsMap - Map of tags by ID
 * @param {Map} optionSetsMap - Map of option sets by ID
 * @param {Map} itemsMap - Map of items by ID
 * @param {Array} categories - All categories from the API
 * @param {string} newTagId - The tag ID for "New"
 * @returns {Object|null} The formatted "Newly Launched" category or null if no products found
 */
function createNewlyLaunchedCategory(items, itemsByCategoryMap, itemTagsMap, optionSetsMap, itemsMap, categories, newTagId) {
  // Group new items by their original subcategory
  const itemsBySubcategory = new Map();
  
  // First, identify all Group items and their variants. A group counts as New
  // when either the parent or any child variant carries the New tag.
  const variantGroups = new Map();
  const groupItems = new Map();

  items.forEach(item => {
    if (item.type === 'Group') {
      groupItems.set(item.itemId, item);
      variantGroups.set(item.itemId, []);
    }
  });

  items.forEach(item => {
    if (item.groupItemId && variantGroups.has(item.groupItemId)) {
      variantGroups.get(item.groupItemId).push(item);
    }
  });

  let hasNewProducts = false;

  const addItemToSubcategory = (item) => {
    const subCategoryId = item.subCategoryId || null;

    if (!itemsBySubcategory.has(subCategoryId)) {
      itemsBySubcategory.set(subCategoryId, []);
    }
    itemsBySubcategory.get(subCategoryId).push(item);
    hasNewProducts = true;
  };

  // Process simple New items. Variants that belong to a group are handled once
  // with their parent group below.
  items.forEach(item => {
    if (item.groupItemId && groupItems.has(item.groupItemId)) {
      return;
    }

    if (item.type === 'Group') {
      return;
    }

    if (hasTag(item, newTagId)) {
      addItemToSubcategory(item);
    }
  });

  // Add grouped products once when the group or any child variant is New.
  groupItems.forEach((groupItem, groupId) => {
    const variants = variantGroups.get(groupId) || [];
    const groupHasNewTag = hasTag(groupItem, newTagId) || variants.some(variant => hasTag(variant, newTagId));

    if (groupHasNewTag && (variants.length > 0 || (groupItem.variantAttributes && groupItem.variantAttributes.length > 0))) {
      const itemWithVariants = {
        ...groupItem,
        _variants: variants.length > 0 ? variants : createSyntheticVariants(groupItem)
      };

      addItemToSubcategory(itemWithVariants);
    }
  });

  if (!hasNewProducts) {
    return null;
  }

  // Build subcategory metadata from all categories
  const allSubCategories = new Map();
  categories.forEach(cat => {
    if (cat.subCategories && Array.isArray(cat.subCategories)) {
      cat.subCategories.forEach(subCat => {
        if (!allSubCategories.has(subCat.subCategoryId)) {
          allSubCategories.set(subCat.subCategoryId, subCat);
        }
      });
    }
  });

  // Format subcategory metadata for items that have subcategories
  const subCategoryMetadata = [];
  itemsBySubcategory.forEach((items, subCategoryId) => {
    if (subCategoryId && allSubCategories.has(subCategoryId)) {
      const subCat = allSubCategories.get(subCategoryId);
      subCategoryMetadata.push({
        name: subCat.name,
        id: subCat.subCategoryId,
      });
    }
  });

  // Sort subcategories by displayOrder
  subCategoryMetadata.sort((a, b) => {
    const subCatA = allSubCategories.get(a.id);
    const subCatB = allSubCategories.get(b.id);
    const orderA = subCatA?.displayOrder !== undefined && subCatA?.displayOrder !== null ? subCatA.displayOrder : Infinity;
    const orderB = subCatB?.displayOrder !== undefined && subCatB?.displayOrder !== null ? subCatB.displayOrder : Infinity;
    return orderA - orderB;
  });

  // Format all products
  const allProducts = [];
  
  itemsBySubcategory.forEach((items, subCategoryId) => {
    const subCat = subCategoryId ? allSubCategories.get(subCategoryId) : null;
    const subCategoryName = subCat ? subCat.name : null;

    items.forEach(item => {
      allProducts.push(formatProduct(item, subCategoryName, 'Newly Launched', itemTagsMap, optionSetsMap, itemsMap));
    });
  });

  // Sort products by displayOrder
  allProducts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return {
    id: 'newly-launched', // Synthetic ID for the "Newly Launched" category
    parentName: 'Newly Launched',
    description: '',
    categoryImage: '',
    subCategories: subCategoryMetadata,
    products: allProducts,
  };
}

/**
 * Create synthetic variant items from a Group item's variantAttributes
 * Used when the API doesn't return actual variant items
 */
function createSyntheticVariants(groupItem) {
  const variants = [];

  if (!groupItem.variantAttributes || groupItem.variantAttributes.length === 0) {
    return variants;
  }

  // Get the first variant attribute (usually the only one, like "Quantity")
  const variantAttr = groupItem.variantAttributes[0];

  if (!variantAttr.values || variantAttr.values.length === 0) {
    return variants;
  }

  // Create a synthetic variant for each value
  variantAttr.values.forEach((value, index) => {
    variants.push({
      itemId: `${groupItem.itemId}_synthetic_${index}`,
      type: 'Variant',
      itemName: groupItem.itemName,
      description: groupItem.description,
      price: groupItem.price || 0, // Use group's price as fallback
      skuCode: `${groupItem.skuCode}_${index}`,
      status: groupItem.status,
      measuringUnit: groupItem.measuringUnit,
      categoryId: groupItem.categoryId,
      subCategoryId: groupItem.subCategoryId,
      itemTagIds: groupItem.itemTagIds || [],
      imageURL: groupItem.imageURL,
      taxTypeIds: groupItem.taxTypeIds || [],
      extraInfo: groupItem.extraInfo || {},
      variantValues: [
        {
          name: variantAttr.name,
          value: value
        }
      ],
      groupItemId: groupItem.itemId,
      isSynthetic: true // Flag to indicate this was generated
    });
  });

  return variants;
}

/**
 * Group items by category and subcategory, handling variant groups
 */
function groupItemsByCategory(items) {
  const map = new Map();

  // First pass: Create a map of group items and their variants
  const variantGroups = new Map();
  const groupItems = new Map();

  items.forEach(item => {
    if (item.type === 'Group') {
      groupItems.set(item.itemId, item);
      variantGroups.set(item.itemId, []);
    }
  });

  // Second pass: Collect variants for each group
  items.forEach(item => {
    if (item.groupItemId && variantGroups.has(item.groupItemId)) {
      variantGroups.get(item.groupItemId).push(item);
    }
  });

  // Third pass: Process items for category grouping
  const processedVariants = new Set();

  items.forEach(item => {
    // Skip individual variant items (they'll be added with their group)
    if (item.groupItemId && groupItems.has(item.groupItemId)) {
      processedVariants.add(item.itemId);
      return;
    }

    // For Group items without variants, check if we can create synthetic variants from variantAttributes
    if (item.type === 'Group' && (!variantGroups.has(item.itemId) || variantGroups.get(item.itemId).length === 0)) {
      // If variantAttributes exist, create synthetic variants
      if (item.variantAttributes && item.variantAttributes.length > 0) {
        const syntheticVariants = createSyntheticVariants(item);
        if (syntheticVariants.length > 0) {
          variantGroups.set(item.itemId, syntheticVariants);
          console.log(`Created ${syntheticVariants.length} synthetic variants for ${item.itemName} (${item.itemId})`);
        } else {
          return; // Skip if no valid variants could be created
        }
      } else {
        return; // Skip Group items without variants or variantAttributes
      }
    }

    const categoryId = item.categoryId;

    if (!map.has(categoryId)) {
      map.set(categoryId, {
        subCategories: new Map(),
        directItems: [],
      });
    }

    const categoryData = map.get(categoryId);

    // If this is a Group item, attach its variants
    let itemToAdd = item;
    if (item.type === 'Group' && variantGroups.has(item.itemId)) {
      itemToAdd = {
        ...item,
        _variants: variantGroups.get(item.itemId)
      };
    }

    // If item has subcategory, group by subcategory
    if (item.subCategoryId) {
      if (!categoryData.subCategories.has(item.subCategoryId)) {
        categoryData.subCategories.set(item.subCategoryId, []);
      }
      categoryData.subCategories.get(item.subCategoryId).push(itemToAdd);
    } else {
      // Item belongs directly to category
      categoryData.directItems.push(itemToAdd);
    }
  });

  return map;
}

/**
 * Format a single category with its subcategories and products
 */
function formatCategory(category, itemsByCategoryMap, itemTagsMap, optionSetsMap, itemsMap) {
  const categoryData = itemsByCategoryMap.get(category.categoryId);

  if (!categoryData) {
    return null;
  }

  // Build subcategory metadata and sort by displayOrder
  const subCategories = category.subCategories || [];
  const subCategoryMetadata = subCategories
    .sort((a, b) => {
      // Sort by displayOrder, treating undefined/null as Infinity (put at end)
      const orderA = a.displayOrder !== undefined && a.displayOrder !== null ? a.displayOrder : Infinity;
      const orderB = b.displayOrder !== undefined && b.displayOrder !== null ? b.displayOrder : Infinity;
      return orderA - orderB;
    })
    .map(subCat => ({
      name: subCat.name,
      id: subCat.subCategoryId,
    }));

  // Format all products (from subcategories and direct)
  const allProducts = [];

  // Add products from subcategories
  categoryData.subCategories.forEach((items, subCategoryId) => {
    const subCategory = subCategories.find(sc => sc.subCategoryId === subCategoryId);
    const subCategoryName = subCategory ? subCategory.name : null;

    items.forEach(item => {
      allProducts.push(formatProduct(item, subCategoryName, category.name, itemTagsMap, optionSetsMap, itemsMap));
    });
  });

  // Add direct items (no subcategory)
  categoryData.directItems.forEach(item => {
    allProducts.push(formatProduct(item, null, category.name, itemTagsMap, optionSetsMap, itemsMap));
  });

  // Sort products by displayOrder
  allProducts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return {
    id: category.categoryId,
    parentName: category.name,
    description: "",
    categoryImage: "",
    subCategories: subCategoryMetadata,
    products: allProducts,
  };
}

/**
 * Format a single product/item
 */
function formatProduct(item, subCategoryName, categoryName, itemTagsMap, optionSetsMap, itemsMap) {
  // Check if this is a Group item with variants
  const isGroupItem = item.type === 'Group' && item._variants && item._variants.length > 0;

  // For Group items, use the first variant's data for most fields EXCEPT image
  const sourceItem = isGroupItem ? item._variants[0] : item;

  // Map item tags. For Group items, merge tags from the parent and every
  // variant so badges/filtering still work when tags live only on variants.
  const tagIds = isGroupItem
    ? mergeUniqueTagIds(item, item._variants)
    : getItemTagIds(sourceItem);
  const tags = formatTagIds(tagIds, itemTagsMap);

  // Transform nutrition data from extraInfo (from source item)
  const nutritions = formatNutritions(sourceItem);

  // Get variations
  let variations = [];
  
  if (isGroupItem) {
    // For Group items, create a variation group for the variants
    const variantOptions = item._variants.map(variant => {
      // Format this variant's option sets
      let variantOptionSets = [];
      if (variant.optionSetIds && variant.optionSetIds.length > 0) {
        variantOptionSets = formatVariations(variant, optionSetsMap, itemTagsMap, itemsMap);
      }
      
      return {
        id: variant.itemId,
        title: formatVariantTitle(variant),
        price: variant.price || 0,
        taxTypeIds: Array.isArray(variant.taxTypeIds) ? variant.taxTypeIds : [],
        isPriceIncludesTax: Boolean(variant.isPriceIncludesTax),
        isStockAvailable: variant.status === 'Active',
        variantValues: variant.variantValues || [],
        skuCode: variant.skuCode || variant.itemId, // Add SKU code for sold-out checking
        // Attach formatted option sets for this variant
        optionSets: variantOptionSets,
      };
    });
    // Sort by price
    variantOptions.sort((a, b) => a.price - b.price);
    
    // Add variant group first
    variations.push({
      optionSetId: item.itemId,
      optionSetName: item.itemName,
      optionSetDescription: "",
      options: variantOptions,
      min: 1,
      max: 1,
      isVariantGroup: true,  // Flag to identify this is a variant group
    });
  }
  
  // For all items, also add option set groups if they have option sets
  // BUT: For Group items, DON'T add product-level options - all options come from variants
  if (!isGroupItem && sourceItem.optionSetIds && sourceItem.optionSetIds.length > 0) {
    const optionSetGroups = formatVariations(sourceItem, optionSetsMap, itemTagsMap, itemsMap);
    variations.push(...optionSetGroups);
  }

  // If no variations were added for Group items, create a default group
  // For Simple items with no option sets, leave variations empty
  if (variations.length === 0 && isGroupItem) {
    variations = [
      {
        optionSetId: item.itemId,
        optionSetName: item.itemName,
        options: [
          {
            id: item.itemId,
            title: item.itemName,
            price: sourceItem.price,
            isStockAvailable: sourceItem.status === 'Active',
          }
        ],
        min: 1,
        max: 1,
      }
    ];
  }

  // Calculate display price
  // For Group items: use cheapest variant price
  // For Simple items: always use base item price (optional option sets don't affect display price)
  let displayPrice = sourceItem.price || 0;
  if (isGroupItem && variations.length > 0 && variations[0].options.length > 0) {
    displayPrice = Math.min(...variations[0].options.map(v => v.price));
  }

  // For images and descriptions, prefer Group item's data if available
  const imageUrl = isGroupItem && item.imageURL ? item.imageURL : (sourceItem.imageURL || "");
  const description = isGroupItem && item.description ? item.description : (sourceItem.description || "");

  return {
    id: item.itemId,
    name: item.itemName || sourceItem.itemName,
    price: displayPrice,
    displayOrder: item.displayOrder || sourceItem.displayOrder,
    imageUrl,
    description,
    nutritions,
    variations,
    isVeg: hasVegTag(tags),
    tags,
    categoryName,
    subCategoryName,
    hasMultipleVariants: variations.length > 0 && variations[0].options.length > 1, // Check if first group has multiple options
    skuCode: item.skuCode || sourceItem.skuCode || item.itemId, // Add SKU for sold-out matching
    taxTypeIds: Array.isArray(sourceItem.taxTypeIds) ? sourceItem.taxTypeIds : [],
    isPriceIncludesTax: Boolean(sourceItem.isPriceIncludesTax),
  };
}

/**
 * Format variant title from variant values
 */
function formatVariantTitle(variant) {
  if (!variant.variantValues || variant.variantValues.length === 0) {
    return variant.itemName;
  }

  // Extract the variant value (e.g., "Medium Dark Roast" from variantValues)
  const variantValue = variant.variantValues[0]?.value;
  if (variantValue && variantValue !== variant.itemName) {
    return variantValue;
  }

  return variant.itemName;
}

/**
 * Format nutrition facts from Rista's extraInfo
 */
function formatNutritions(item) {
  const nutritions = [];
  const extraInfo = item.extraInfo;

  if (!extraInfo) {
    return nutritions;
  }

  // Map Rista nutrition fields to our format
  const nutritionMapping = [
    { key: 'calorieCount', name: 'CalorieCount', unit: 'kcal' },
    { key: 'portionSize', name: 'PortionSize', unit: '' },
    { key: 'portionSizeUnit', name: 'PortionSizeUnit', unit: '' },
    { key: 'carbohydrates', name: 'Carbohydrates', unit: 'g' },
    { key: 'totalFat', name: 'TotalFat', unit: 'g' },
    { key: 'transFat', name: 'TransFat', unit: 'g' },
    { key: 'cholesterol', name: 'Cholesterol', unit: 'mg' },
    { key: 'protein', name: 'Protein', unit: 'g' },
    { key: 'sodium', name: 'Sodium', unit: 'mg' },
    { key: 'sugar', name: 'Sugar', unit: 'g' },
    { key: 'saturated', name: 'Saturated', unit: 'g' },
    { key: 'fiber', name: 'Fiber', unit: 'g' },
    { key: 'artificialSweetener', name: 'ArtificialSweetener', unit: 'g' },
    { key: 'serves', name: 'Serves', unit: '' },
    { key: 'serveSizeUnit', name: 'ServeSizeUnit', unit: '' },
  ];

  nutritionMapping.forEach(({ key, name, unit }) => {
    const value = extraInfo[key];
    if (value !== undefined && value !== null && value !== '') {
      nutritions.push({
        nutritionName: name,
        nutritionValue: String(value),
        units: unit,
      });
    }
  });

  // Handle allergy information
  if (extraInfo.allergyInformation && Array.isArray(extraInfo.allergyInformation)) {
    nutritions.push({
      nutritionName: 'AllergyInformation',
      nutritionValue: extraInfo.allergyInformation.join(', '),
      units: '',
    });
  }

  return nutritions;
}

/**
 * Format variations from option sets - grouped by optionSetName
 */
function formatVariations(item, optionSetsMap, itemTagsMap, itemsMap) {
  const variationGroups = [];

  // If item has option sets, convert them to grouped variations
  if (item.optionSetIds && item.optionSetIds.length > 0) {
    item.optionSetIds.forEach(optionSetId => {
      const optionSet = optionSetsMap.get(optionSetId);

      if (optionSet && optionSet.options && optionSet.options.length > 0) {
        const options = optionSet.options.map(option => {
          // Look up the item by the option's itemId to get its tags
          const optionItem = itemsMap.get(option.itemId);

          // Map option tags from the referenced item
          const optionTags = optionItem && optionItem.itemTagIds
            ? optionItem.itemTagIds
              .map(tagId => itemTagsMap.get(tagId))
              .filter(tag => tag !== undefined)
            : [];

          return {
            id: option.optionId,
            optionId: option.optionId,
            title: option.optionName,
            name: option.optionName,
            itemId: option.itemId,
            skuCode: optionItem?.skuCode || option.skuCode || option.itemId || option.optionId,
            basePrice: item.price,           // Store base price separately
            optionPrice: option.price || 0,  // Store just the option price
            unitPrice: option.price || 0,
            price: item.price + (option.price || 0), // Keep total for calculations
            isDefault: Boolean(option.isDefault),
            isStockAvailable: true,
            isOptionSet: true,  // Flag to indicate this is an option set item
            tags: optionTags,     // Add tags for the option
          };
        });

        variationGroups.push({
          optionSetId: optionSetId,
          optionSetName: optionSet.displayName || optionSet.name,
          optionSetDescription: optionSet.description || "",
          options: options,
          min: optionSet.min || 0,
          max: optionSet.max || Infinity,
          isOptionSetGroup: true,  // Flag for the entire group
        });
      }
    });
  }

  // If no variations from option sets, create a default variation
  if (variationGroups.length === 0) {
    variationGroups.push({
      optionSetId: item.itemId,
      optionSetName: item.itemName,
      options: [
        {
          id: item.itemId,
          title: item.itemName,
          price: item.price,
          isStockAvailable: item.status === 'Active',
        }
      ],
      min: 1,
      max: 1,
    });
  }

  return variationGroups;
}

/**
 * Format tags for filtering - only include tags that are actually used
 * @param {Array} itemTags - All available tags
 * @param {Set} usedTagIds - Set of tag IDs that are actually used by items
 * @param {string|null} newTagId - The tag ID for "New" tag to exclude from filter bar
 */
function formatTags(itemTags, usedTagIds) {
  return itemTags
    .filter(tag => usedTagIds.has(tag.itemTagId)) // Only include tags that are used
    .map(tag => ({
      id: tag.itemTagId,
      name: tag.name,
      description: "",
      categoryImage: TAG_ICON_URLS[tag.itemTagId] || "", // Use tag icon URL if available
      products: [],
      isTag: true,
    }));
}

/**
 * Check if product has vegetarian tag
 */
function hasVegTag(tags) {
  return tags.some(tag => {
    const name = tag.name?.toLowerCase() || '';
    return name.includes('veg') && !name.includes('non');
  });
}

/**
 * Load and transform Rista menu from API endpoint
 * @param {string} apiBaseUrl - Base URL of the Rista API
 * @param {string} branchCode - Branch/Cafe code to fetch menu for
 * @returns {Promise<Object>} Transformed menu data
 */
export async function loadRistaMenuFromAPI(apiBaseUrl, branchCode) {
  try {
    const url = `${apiBaseUrl}?requestType=menu&channel=Dine In GotTea&branchCode=${encodeURIComponent(branchCode)}`;
    console.log(`Fetching Rista menu from: ${url}`);
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load menu: ${response.status} ${response.statusText}`);
    }
    
    const ristaData = await response.json();
    console.log('Rista API response received:', ristaData);
    
    return transformRistaMenuData(ristaData.data);
  } catch (error) {
    console.error('Error loading Rista menu from API:', error);
    throw error;
  }
}
